import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Search,
  MoreVert,
  Block,
  CheckCircle,
  AdminPanelSettings,
  PersonOutline,
  Refresh,
  Star,
  AccountCircle
} from '@mui/icons-material';
import { useRole, ROLES } from '../../contexts/RoleContext';
import { formatDate } from './utils/formatters';
import usePagination from './hooks/usePagination';
import useSearch from './hooks/useSearch';
import adminService from '../../services/adminService';
import styles from './styles/UserManagement.module.css';

const UserManagement = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('role'); // 'role' or 'subscription'
  const [editRole, setEditRole] = useState('');
  const [editSubscription, setEditSubscription] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const { updateUserRole, updateUserSubscription } = useRole();

  // Fetch users data
  const fetchUsers = async (currentPage = 0, pageSize = 10, search = '') => {
    try {
      setLoading(true);
      const response = await adminService.getUsers({
        page: currentPage + 1, // API expects 1-based pagination
        limit: pageSize,
        search
      });
      setUsers(response.users);
      setTotalUsers(response.pagination.total);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
      showSnackbar('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load users on component mount and when pagination/search changes
  useEffect(() => {
    fetchUsers(page, rowsPerPage, searchTerm);
  }, [page, rowsPerPage, searchTerm]);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleMenuClick = (event, user) => {
    setSelectedUser(user);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditRole = () => {
    setDialogType('role');
    setEditRole(selectedUser.role);
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleEditSubscription = () => {
    setDialogType('subscription');
    setEditSubscription(selectedUser.subscription || 'basic');
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setEditRole('');
    setEditSubscription('');
    setDialogType('role');
  };

  const handleSaveChanges = async () => {
    try {
      if (dialogType === 'role') {
        await adminService.updateUserRole(selectedUser.id, editRole);
        showSnackbar(`User role updated to ${editRole}`, 'success');
      } else if (dialogType === 'subscription') {
        await adminService.updateUserSubscription(selectedUser.id, editSubscription);
        showSnackbar(`User subscription updated to ${editSubscription}`, 'success');
      }
      handleDialogClose();
      // Refresh the user list
      fetchUsers(page, rowsPerPage, searchTerm);
    } catch (error) {
      console.error('Error updating user:', error);
      showSnackbar(`Failed to update user ${dialogType}`, 'error');
    }
  };

  const handleRefresh = () => {
    fetchUsers(page, rowsPerPage, searchTerm);
  };

  const getSubscriptionChip = (subscription) => {
    const isPro = subscription === 'pro';
    return (
      <Chip
        icon={isPro ? <Star /> : <AccountCircle />}
        label={isPro ? 'Pro' : 'Basic'}
        color={isPro ? 'warning' : 'default'}
        size="small"
        sx={isPro ? { backgroundColor: 'gold', color: 'black' } : {}}
      />
    );
  };

  const renderUserRow = (user) => (
    <TableRow key={user.id}>
      <TableCell>{user.name}</TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell>
        <Chip
          icon={user.role === ROLES.ADMIN ? <AdminPanelSettings /> : <PersonOutline />}
          label={user.role === ROLES.PRO_CUSTOMER ? 'Pro Customer' : user.role}
          color={user.role === ROLES.ADMIN ? 'error' : user.role === ROLES.PRO_CUSTOMER ? 'warning' : 'default'}
          size="small"
        />
      </TableCell>
      <TableCell>
        {getSubscriptionChip(user.subscription)}
      </TableCell>
      <TableCell>
        <Chip
          icon={user.status === 'active' ? <CheckCircle /> : <Block />}
          label={user.status}
          color={user.status === 'active' ? 'success' : 'default'}
          size="small"
        />
      </TableCell>
      <TableCell>{formatDate(user.lastLogin)}</TableCell>
      <TableCell>{formatDate(user.joinDate)}</TableCell>
      <TableCell align="right">
        <IconButton onClick={(e) => handleMenuClick(e, user)}>
          <MoreVert />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2" fontWeight="bold">
            User Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search users by name, email, or role..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Subscription</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Join Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(renderUserRow)}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditRole}>Change Role</MenuItem>
        <MenuItem onClick={handleEditSubscription}>Manage Subscription</MenuItem>
        <MenuItem onClick={handleMenuClose}>Reset Password</MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          Deactivate Account
        </MenuItem>
      </Menu>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogType === 'role' ? 'Change User Role' : 'Manage User Subscription'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {dialogType === 'role' ? (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={editRole}
                label="Role"
                onChange={(e) => setEditRole(e.target.value)}
              >
                {Object.values(ROLES).map((role) => (
                  <MenuItem key={role} value={role}>
                    {role === ROLES.PRO_CUSTOMER ? 'Pro Customer' : role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Subscription</InputLabel>
              <Select
                value={editSubscription}
                label="Subscription"
                onChange={(e) => setEditSubscription(e.target.value)}
              >
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="pro">Pro</MenuItem>
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSaveChanges} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement; 